import {
  type AugmentationComponent,
  type AugmentationToolbarChildOption,
  type AugmentationToolbarOption
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { useMemo } from "react"
import { match } from "react-router-dom"
import {
  useManageMentorGroupMembershipsQuery,
  useMentorGroupQuery,
  useUserPathReportCardQuery,
  useUserProfileQuery
} from "@pathwright/graphql-client/src/generated"
import AssignAugmentation from "../../assign/AssignAugmentation"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import ProfileAugmentation from "../../profile/augmentation/ProfileAugmentation"
import ProfileRegistrationConfigureAugmentation from "../../profile/augmentation/ProfileRegistrationConfigureAugmentation"
import ProfileRegistrationReportCardAugmentation from "../../profile/augmentation/ProfileRegistrationReportCardAugmentation"
import { useCanViewSimpleNav } from "../../unified-home/view-permission/useUnifiedHomePermissions"
import { canViewMentorGroups } from "../../user/permissions"
import GroupAddAugmentation from "./GroupAddAugmentation"
import GroupAugmentation from "./GroupAugmentation"
import GroupManageAugmentation from "./GroupManageAugmentation"
import GroupMemberMembershipAugmentation from "./GroupMemberMembershipAugmentation"
import GroupMembersAugmentation from "./GroupMembersAugmentation"

function parseGroupId(value: string | undefined): number | null {
  if (!value) return null
  const parsed = parseInt(value)
  return isNaN(parsed) ? null : parsed
}

// Nested group routes name the param groupId1, groupId2, … The current group
// is always the deepest of those on the match.
function getDeepestGroupId(
  routeMatch: match<{ [key: string]: string }> | undefined
): number | null {
  if (!routeMatch) return null

  let deepestN = 0
  let deepest: string | undefined
  for (const [key, value] of Object.entries(routeMatch.params)) {
    const depthMatch = /^groupId(\d+)$/.exec(key)
    if (!depthMatch) continue
    const depth = parseInt(depthMatch[1], 10)
    if (depth >= deepestN) {
      deepestN = depth
      deepest = value
    }
  }

  return parseGroupId(deepest)
}

const GroupMembersAugmentationComponent: AugmentationComponent = ({
  route
}) => {
  const groupId = getDeepestGroupId(route?.match)
  return groupId ? <GroupMembersAugmentation groupId={groupId} /> : <></>
}

const GroupAddAugmentationComponent: AugmentationComponent = ({ route }) => {
  const groupId = getDeepestGroupId(route?.match)
  return <GroupAddAugmentation parentGroupId={groupId} />
}

const GroupManageAugmentationComponent: AugmentationComponent = ({ route }) => {
  const groupId = getDeepestGroupId(route?.match)
  return groupId ? <GroupManageAugmentation groupId={groupId} /> : <></>
}

const GroupMemberMembershipAugmentationComponent: AugmentationComponent = ({
  route
}) => {
  const groupId = getDeepestGroupId(route?.match)
  return groupId ? (
    <GroupMemberMembershipAugmentation groupId={groupId} />
  ) : (
    <></>
  )
}

const GroupNameLabel: NonNullable<
  AugmentationToolbarChildOption["AugmentationLabelComponent"]
> = ({ match: routeMatch }) => {
  const groupId = getDeepestGroupId(routeMatch)
  const groupQuery = useMentorGroupQuery({
    variables: { id: groupId! },
    skip: !groupId
  })
  const groupName = groupQuery.data?.school?.mentor_group?.name || null
  return <>{groupName}</>
}

const GroupMemberLabel: NonNullable<
  AugmentationToolbarChildOption["AugmentationLabelComponent"]
> = ({ match: routeMatch }) => {
  const userId = parseInt(routeMatch.params.userId)
  const userProfileQuery = useUserProfileQuery({
    variables: {
      id: userId
    }
  })
  const userName = userProfileQuery.data?.user?.display_name || null
  return <>{userName}</>
}

const GroupPathLabel: NonNullable<
  AugmentationToolbarChildOption["AugmentationLabelComponent"]
> = ({ match: routeMatch }) => {
  const pathId = parseInt(routeMatch.params.pathId)!
  const userPathReportCardQuery = useUserPathReportCardQuery({
    variables: {
      id: pathId
    }
  })

  const pathName = userPathReportCardQuery.data?.path?.cohort?.name || null

  return <>{pathName}</>
}

function getGroupAugmentationForDepth(depth: number = 5) {
  // Use an inner function to recurse upwards to depth (otherwise we could avoid an inner
  // function if recursing downwards to 0).
  function inner(currentDepth: number = 1) {
    const groupIdVar = `groupId${currentDepth}`

    const augmentations: AugmentationToolbarChildOption = {
      key: `:${groupIdVar}`,
      AugmentationLabelComponent: GroupNameLabel,
      label: "Group",
      AugmentationComponent: GroupMembersAugmentationComponent,
      children: [
        {
          key: "add-group",
          label: "Add Group",
          AugmentationComponent: GroupAddAugmentationComponent
        },
        {
          key: "manage",
          label: "Manage",
          AugmentationComponent: GroupManageAugmentationComponent
        },
        {
          key: ":manageMember(manage-member-\\d+)",
          label: "Configure Membership",
          AugmentationComponent: GroupMemberMembershipAugmentationComponent
        },
        {
          key: "member/:userId",
          label: "Group Member",
          AugmentationLabelComponent: GroupMemberLabel,
          AugmentationComponent: ProfileAugmentation,
          children: [
            {
              key: "registration/:registrationId",
              label: "Registration Settings",
              AugmentationComponent: ProfileRegistrationConfigureAugmentation
            },
            {
              key: "add-to-path",
              label: "Add to Path",
              AugmentationComponent: AssignAugmentation
            },
            {
              key: ":pathId",
              label: "Path",
              AugmentationLabelComponent: GroupPathLabel,
              AugmentationComponent: ProfileRegistrationReportCardAugmentation
            }
          ]
        }
      ]
    }

    if (currentDepth < depth) {
      // NOTE: group route must be last
      augmentations.children?.push(inner(currentDepth + 1))
    }

    return augmentations
  }

  return inner(1)
}

const nestedGroupAugmentations = getGroupAugmentationForDepth(5)

function useGroupAugmentationToolbarOption(): AugmentationToolbarOption | null {
  const pwContext = usePathwrightContext()
  const userCanViewMentorGroups = canViewMentorGroups(pwContext)
  const userCanViewSimpleNav = useCanViewSimpleNav()
  const query = useManageMentorGroupMembershipsQuery({
    skip: !userCanViewMentorGroups
  })

  // Check if the user has only 1, admin membership and if so,
  // link directly to the related mentor group.
  const total = query.data?.me?.mentorGroupMemberships?.total
  const adminMembershipEdge =
    query.data?.me?.mentorGroupMemberships?.edges?.find(
      (e) => e?.node?.role === 15
    )
  const groupId =
    total === 1 && adminMembershipEdge
      ? adminMembershipEdge.node?.group_id
      : null

  const pathname = groupId
    ? `/community/groups/${groupId}/`
    : "/community/groups/"

  return useMemo(
    () =>
      userCanViewMentorGroups
        ? userCanViewSimpleNav
          ? {
              key: "groups",
              icon: "group-stack",
              tLabel: "Your group_plural",
              AugmentationComponent: GroupAugmentation,
              children: [
                {
                  key: "add-group",
                  label: "Add Group",
                  AugmentationComponent: GroupAddAugmentationComponent
                },
                nestedGroupAugmentations
              ]
            }
          : {
              key: "groups",
              icon: "group-stack",
              label: "Groups",
              to: pathname,
              // HACK: (well sort of), we don't want to return to the home
              // augmentation panel (if it was open previously) when closing
              // the mentor groups augmentation.
              onClick: () => {
                setTimeout(() => {
                  // @ts-expect-error
                  window.App.getStore("navigation")._mutate({
                    modal_launched_from_url: "/home"
                  })
                }, 100)
              }
            }
        : null,
    [userCanViewMentorGroups, userCanViewSimpleNav, pathname]
  )
}

export default useGroupAugmentationToolbarOption
